import { useCallback } from 'react'

import type { FilterUIState } from 'features/filters/store'
import { useFilterUIStore } from 'features/filters/store'
import { FilterOptionSelect } from 'features/filters/types'
import {
  normalizeThemeName,
  splitThemeName,
} from 'features/project/normalize-theme-name'

const useThemes = (): {
  getSubthemeNameFromClusterUUID: (uuid: string) => string | undefined
  getClusterUUIDsFromThemeName: (theme: string, subtheme: string) => string[]
  getNormalizedThemeName: (theme: string) => string
  convertThemesToClusterUUIDs: (themeNames: string[]) => string[]
} => {
  const defaultFilterList = useFilterUIStore(
    (state: FilterUIState) => state.defaultFilterList,
  )

  const getSubthemeNameFromClusterUUID = useCallback(
    (uuid: string) => {
      const matchedValue = (
        defaultFilterList.find(
          (item) => item.field === 'theme',
        ) as FilterOptionSelect
      )?.values.find((el) => el.cluster_uuid === uuid)?.value
      if (!matchedValue) {
        return undefined
      }
      return splitThemeName(matchedValue).subtheme
    },
    [defaultFilterList],
  )

  const getClusterUUIDsFromThemeName = useCallback(
    (theme: string, subtheme: string) => {
      const normalizedTheme = theme.trim().toLowerCase()
      const normalizedSubtheme = subtheme.trim().toLowerCase()

      return (
        (
          defaultFilterList.find(
            (item) => item.field === 'theme',
          ) as FilterOptionSelect
        )?.values
          ?.filter((el) => {
            const parsed = splitThemeName(el.value)
            return (
              parsed.theme.trim().toLowerCase() === normalizedTheme &&
              parsed.subtheme.trim().toLowerCase() === normalizedSubtheme
            )
          })
          ?.map((el) => el.cluster_uuid ?? el.value) ?? [
          `${theme}: ${subtheme}`,
        ]
      )
    },
    [defaultFilterList],
  )

  const getNormalizedThemeName = useCallback(normalizeThemeName, [])

  const convertThemesToClusterUUIDs = useCallback(
    (themeNames: string[]) => {
      return themeNames
        .map((name: string) => {
          // theme labels take the format [theme]: [subtheme]
          // uuids are xxxxxx-xxxxxx-xxxxxx-xxxxxx
          // just checking to see what format the theme has been passed in as
          const parts = name.split(': ')
          if (parts.length < 2) return name
          return getClusterUUIDsFromThemeName(parts[0]!, parts[1]!)
        })
        .flat()
    },
    [getClusterUUIDsFromThemeName],
  )

  return {
    getSubthemeNameFromClusterUUID,
    getClusterUUIDsFromThemeName,
    getNormalizedThemeName,
    convertThemesToClusterUUIDs,
  }
}

export default useThemes
